(() => {
  const cartUrl = () => {
    if (typeof routes !== 'undefined' && routes?.cart_url) return routes.cart_url;
    return '/cart';
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartUrl()}.json`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    const cart = await response.json();
    if (!cart?.currency) throw new Error('Cart response is missing currency');

    return cart;
  };

  let cartSnapshotPromise = fetchCart().catch(() => null);

  const normalizeShopifyId = (value) => {
    const id = String(value ?? '').trim();
    return id ? id.split('/').pop() || null : null;
  };

  const moneyAmount = (value) => {
    const amount = value && typeof value === 'object' ? value.amount : value;
    const number = Number(amount);
    return amount == null || amount === '' || !Number.isFinite(number) ? null : number;
  };

  const lineDetails = (line) => {
    if (!line) return {};

    const merchandise = line.merchandise || {};
    const product = merchandise.product || line.product || {};
    const ajaxPrice = line.final_price ?? line.price;
    const price = line.cost?.amountPerQuantity
      ? moneyAmount(line.cost.amountPerQuantity)
      : ajaxPrice == null
        ? null
        : moneyAmount(ajaxPrice) / 100;

    return {
      product_id: normalizeShopifyId(product.id || line.product_id),
      product_name: product.title || line.product_title || line.title || null,
      product_url: line.url || product.url || null,
      variant_id: normalizeShopifyId(merchandise.id || line.merchandiseId || line.variant_id),
      variant_name: merchandise.title || line.variant_title || null,
      sku: merchandise.sku || line.sku || null,
      price,
    };
  };

  const cartLines = (cart) => cart?.lines || cart?.items || [];

  const findCartLine = (cart, changedLine) => {
    const changedLineId = String(changedLine?.id || '');
    const requestedVariantId = normalizeShopifyId(changedLine?.merchandiseId);

    return cartLines(cart).find((line) => {
      const lineId = String(line.id || line.key || '');
      const variantId = lineDetails(line).variant_id;
      return (
        (changedLineId && lineId === changedLineId) ||
        (requestedVariantId && variantId === requestedVariantId)
      );
    });
  };

  const createCartLinesUpdate = (eventTarget, lines, context = 'product') => {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};

    if (
      !CartLinesUpdateEvent ||
      !eventTarget?.dispatchEvent ||
      !Array.isArray(lines) ||
      lines.length === 0
    ) {
      return null;
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    eventTarget.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines,
        promise: deferred.promise,
      }),
    );

    return deferred;
  };

  const resolveCartLinesUpdate = async (deferred) => {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!deferred || !CartLinesUpdateEvent) return;

    try {
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      });
    } catch (error) {
      deferred.reject(error);
    }
  };

  const sendViewCart = async (eventTarget) => {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent || !eventTarget?.dispatchEvent) return;

    try {
      const cart = await fetchCart();
      eventTarget.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        }),
      );
    } catch (_error) {
      // Cart-view analytics are informational and must never interrupt the cart UI.
    }
  };

  const publishWhenReady = (eventName, data, attempt = 0) => {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, data);
      return;
    }
    if (attempt < 100) {
      window.setTimeout(() => publishWhenReady(eventName, data, attempt + 1), 100);
    }
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!event.promise || !Array.isArray(event.lines) || event.lines.length === 0) return;

    const cartBefore = cartSnapshotPromise;
    Promise.all([cartBefore, event.promise])
      .then(async ([before, result]) => {
        if (result?.userErrors?.length || !result?.cart) return;

        // Shopify's normalized StandardEvents cart can omit the AJAX product
        // fields needed for product-level analytics. Re-read the authoritative
        // cart after the confirmed mutation and fall back to the normalized
        // response if that request is unavailable.
        const authoritativeAfter = await fetchCart().catch(() => null);
        const after = authoritativeAfter || result.cart;
        const afterMoney = result.cart.cost?.totalAmount;
        const afterValue = afterMoney
          ? moneyAmount(afterMoney)
          : after.total_price == null
            ? null
            : Number(after.total_price) / 100;
        const currency = afterMoney?.currencyCode || after.currency || before?.currency || null;

        if (event.action === 'add') {
          event.lines.forEach((changedLine) => {
            const quantity = Number(changedLine.quantity || 0) || 1;
            const details = lineDetails(
              findCartLine(after, changedLine) || findCartLine(result.cart, changedLine),
            );
            publishWhenReady('bahimi:add_to_cart', {
              ...details,
              variant_id: details.variant_id || normalizeShopifyId(changedLine.merchandiseId),
              quantity,
              value: details.price == null ? null : details.price * quantity,
              cart_value: afterValue,
              currency,
            });
          });
          return;
        }

        if (!before) return;
        event.lines.forEach((changedLine) => {
          const beforeLine = findCartLine(before, changedLine);
          const afterLine = findCartLine(after, {
            ...changedLine,
            merchandiseId: changedLine.merchandiseId || beforeLine?.variant_id,
          }) || findCartLine(result.cart, {
            ...changedLine,
            merchandiseId: changedLine.merchandiseId || beforeLine?.variant_id,
          });
          const updatedQuantity =
            changedLine.quantity == null ? Number(afterLine?.quantity || 0) : Number(changedLine.quantity);
          const removedQuantity = Math.max(
            0,
            Number(beforeLine?.quantity || 0) - updatedQuantity,
          );
          if (!removedQuantity) return;

          const details = lineDetails(beforeLine);
          publishWhenReady('bahimi:remove_from_cart', {
            ...details,
            variant_id: details.variant_id || normalizeShopifyId(changedLine.merchandiseId),
            quantity: removedQuantity,
            value: details.price == null ? null : details.price * removedQuantity,
            cart_value: afterValue,
            currency,
          });
        });
      })
      .catch(() => undefined)
      .finally(() => {
        cartSnapshotPromise = fetchCart().catch(() => null);
      });
  });
  document.addEventListener('bahimi:cart:remove-confirmed', (event) => {
    publishWhenReady('bahimi:remove_from_cart', event.detail || {});
  });

  const cardDetails = (element) => {
    const card = element?.closest?.('[data-bh-card]');
    if (!card) return {};

    return {
      product_handle: card.getAttribute('data-bh-product-handle') || null,
      variant_id: card.getAttribute('data-bh-selected-variant') || null,
      product_name: card.getAttribute('data-bh-ga4-item-name') || null,
      variant_name: card.getAttribute('data-bh-ga4-item-variant') || null,
      price: Number(card.getAttribute('data-bh-ga4-item-price')) || null,
      item_list_id: card.getAttribute('data-bh-ga4-item-list-id') || null,
      item_list_name: card.getAttribute('data-bh-ga4-item-list-name') || null,
      item_index: Number(card.getAttribute('data-bh-ga4-item-index')) || null,
    };
  };

  document.addEventListener('click', (event) => {
    const productLink = event.target.closest('[data-bh-card-link]');
    if (productLink) {
      publishWhenReady('bahimi:select_item', {
        ...cardDetails(productLink),
        destination_url: productLink.href || null,
      });
      return;
    }

    const quickView = event.target.closest('[data-bh-quickview]');
    if (quickView) {
      publishWhenReady('bahimi:quick_view', cardDetails(quickView));
      return;
    }

    const sortOption = event.target.closest('[data-bh-sort]');
    if (sortOption) {
      publishWhenReady('bahimi:sort_used', {
        sort_key: sortOption.getAttribute('data-bh-sort') || null,
        collection_path: window.location.pathname,
      });
      return;
    }

    const sizeGuide = event.target.closest(
      '[data-bh-size-guide-open], [data-bh-guide-toggle], .bhp-sizeguide-link',
    );
    if (sizeGuide) {
      publishWhenReady('bahimi:size_guide_view', {
        source_location: sizeGuide.matches('.bhp-sizeguide-link') ? 'product_link' : 'modal',
        product_path: window.location.pathname,
      });
    }
  }, true);

  document.addEventListener('change', (event) => {
    const filter = event.target.closest('form[id^="BHFacetsForm-"] input[type="checkbox"]');
    if (!filter) return;

    publishWhenReady('bahimi:filter_used', {
      filter_name: filter.name || null,
      filter_value: filter.value || null,
      selected: Boolean(filter.checked),
      collection_path: window.location.pathname,
    });
  }, true);

  document.addEventListener('bahimi:wishlist-change', (event) => {
    const detail = event.detail || {};
    publishWhenReady(detail.saved ? 'bahimi:add_to_wishlist' : 'bahimi:remove_from_wishlist', {
      wishlist_id: detail.item?.id || detail.id || null,
      product_name: detail.item?.title || null,
      product_url: detail.item?.url || null,
      wishlist_count: detail.count ?? null,
    });
  });


  const publishedDiscoveryEvents = new Set();

  const publishDiscoveryEvent = (key, eventName, data) => {
    if (publishedDiscoveryEvents.has(key) || !window.Shopify?.analytics?.publish) return false;

    window.Shopify.analytics.publish(eventName, data);
    publishedDiscoveryEvents.add(key);
    return true;
  };

  const publishDiscoveryPage = () => {
    const collectionElement = document.querySelector('collection-component[view-event-payload]');
    if (collectionElement) {
      try {
        const collection = JSON.parse(collectionElement.getAttribute('view-event-payload'))?.collection;
        if (collection) {
          const collectionId = normalizeShopifyId(collection.id);
          publishDiscoveryEvent(`collection:${collectionId || collection.handle}`, 'bahimi:view_item_list', {
            collection_id: collectionId,
            collection_handle: collection.handle || null,
            products_count: collection.productsCount ?? null,
          });
        }
      } catch (_error) {
        // Invalid analytics markup must never interrupt the collection page.
      }
    }

    const params = new URLSearchParams(window.location.search);
    const searchTerm = params.get('q');
    if (window.location.pathname === '/search' && searchTerm) {
      const countMatch = document.title.match(/Search:\s*([\d,]+)\s+results?/i);
      const resultsCount = countMatch ? Number(countMatch[1].replace(/,/g, '')) : null;
      publishDiscoveryEvent(`search:${searchTerm}:${window.location.search}`, 'bahimi:search', {
        search_term: searchTerm,
        results_count: resultsCount,
        sort_key: params.get('sort_by'),
      });
    }
  };

  document.addEventListener('shopify:collection:view', (event) => {
    if (!event.collection) return;

    const collectionId = normalizeShopifyId(event.collection.id);
    publishDiscoveryEvent(`collection:${collectionId || event.collection.handle}`, 'bahimi:view_item_list', {
      collection_id: collectionId,
      collection_handle: event.collection.handle || null,
      products_count: event.collection.productsCount ?? null,
    });
  });

  document.addEventListener('shopify:search:update', (event) => {
    if (!event.search) return;

    Promise.resolve(event.promise)
      .then((result) => {
        publishDiscoveryEvent(`search:${event.search.query}:${window.location.search}`, 'bahimi:search', {
          search_term: event.search.query || null,
          results_count: result?.totalCount ?? null,
          sort_key: event.search.sortKey || null,
        });
      })
      .catch(() => undefined);
  });

  const scheduleDiscoveryPage = () => {
    window.setTimeout(publishDiscoveryPage, 1500);
  };

  if (document.readyState === 'complete') {
    scheduleDiscoveryPage();
  } else {
    window.addEventListener('load', scheduleDiscoveryPage, { once: true });
  }

  const publishNewsletterSuccess = () => {
    const success = document.querySelector('[data-bh-email-signup-success]');
    if (!success) return;

    publishWhenReady('bahimi:newsletter_signup', {
      form_location: success.closest('.bh-footer') ? 'footer' : 'newsletter_section',
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', publishNewsletterSuccess, { once: true });
  } else {
    publishNewsletterSuccess();
  }

  window.BahimiAnalytics = Object.freeze({
    createCartLinesUpdate,
    lineDetails,
    normalizeShopifyId,
    resolveCartLinesUpdate,
    sendViewCart,
  });
})();
